import React from 'react';

import { Flex } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import {
  useReviewCreationData,
  useElements,
  useElementsSameSetting,
  setElementsSameSettingAsFeedbackFormId,
  useElementsScoreSettings,
} from 'reducers/reviewCreationSlice';

import { isScorePercentValidator, isScoreValidator } from 'uiToolkitV1/ScoreSettingTable/ScoreSettingTable';

import { useGetElements, useSaveElements } from '../../Hooks/dataHooks/elementsDataHooks';
import { ReviewCreationFooter } from '../../ReviewCreationFooter';
import { MultiViewStepAccordionWrapper } from '../../components/MultiViewStepAccordionWrapper';
import { SkeletonLoaderMultiViewVariant } from '../../components/SkeletonLoaderReviewCreation';
import { GoalSettings } from './GoalSettings';
import { TemplateSelection } from './TemplateSelection';

export const hasAllTemplatesSelected = ({ elements }) =>
  elements.every(({ elementSettings }) =>
    // if (!!settingSameAsFormId) return true;
    elementSettings.providerTypeToTemplateMap.every(({ template }) => !!template)
  );

export const hasAllValidScoring = ({ elements }) => {
  if (elements.length === 0) return false;
  return elements.every(({ elementSettings }) => {
    // if (!!settingSameAsFormId) return true;
    if (!elementSettings.elementScoreSettings.isGoalRatingAutoComputed) return true;
    return (
      isScorePercentValidator(elementSettings.elementScoreSettings.autoComputeGoalRatingSettings) &&
      isScoreValidator(elementSettings.elementScoreSettings.autoComputeGoalRatingSettings)
    );
  });
};

export const hasValidRating = ({ isGoalRatingAutoComputed, autoComputeGoalRatingSettings }) =>
  isGoalRatingAutoComputed && autoComputeGoalRatingSettings.length === 0;

const Footer = () => {
  const elementsViews = useElements();
  const saveDataMutation = useSaveElements();

  const feedbackFormId = elementsViews?.map((item) => item.feedbackFormId);
  const { isGoalRatingAutoComputed, autoComputeGoalRatingSettings } = useElementsScoreSettings({
    feedbackFormId: feedbackFormId[0],
  });

  const isContinueDisabled =
    !hasAllTemplatesSelected({ elements: elementsViews }) ||
    !hasAllValidScoring({ elements: elementsViews }) ||
    hasValidRating({ isGoalRatingAutoComputed, autoComputeGoalRatingSettings }) ||
    saveDataMutation.isLoading;

  const handleContinue = () => {
    saveDataMutation.mutate();
  };

  return <ReviewCreationFooter isContinueDisabled={isContinueDisabled} onContinue={handleContinue} />;
};

const SingleViewSettings = ({ view, feedbackFormId, feedbackFormName, index: viewIndex, singleViewOnly }) => {
  const settingSameAsFormId = useElementsSameSetting({ feedbackFormId });

  const dispatch = useDispatch();

  const elementsViews = useElements();
  const firstViewName = elementsViews?.[0]?.feedbackFormName;

  const firstFeedbackFormId = elementsViews?.[0]?.feedbackFormId;

  const { isAllowSameSettingAsFirstView } = elementsViews[viewIndex];

  const onChangeHandler = () => {
    dispatch(
      setElementsSameSettingAsFeedbackFormId({
        feedbackFormId,
        settingSameAsFormId: settingSameAsFormId ? null : firstFeedbackFormId,
      })
    );
  };

  if (singleViewOnly) {
    return (
      <Flex flexDirection="column" gap={4}>
        <TemplateSelection feedbackFormId={feedbackFormId} />
        <GoalSettings feedbackFormId={feedbackFormId} />
      </Flex>
    );
  }

  return (
    <MultiViewStepAccordionWrapper
      firstViewName={firstViewName}
      feedbackFormName={feedbackFormName}
      onChangeHandler={onChangeHandler}
      settingSameAsFormId={settingSameAsFormId}
      view={view}
      index={viewIndex}
      isAllowSameSettingAsFirstView={isAllowSameSettingAsFirstView}
    >
      <Flex flexDirection="column" gap={4}>
        <TemplateSelection feedbackFormId={feedbackFormId} />
        <GoalSettings feedbackFormId={feedbackFormId} />
      </Flex>
    </MultiViewStepAccordionWrapper>
  );
};

const Elements = () => {
  const { reviewCycleId } = useReviewCreationData();
  const elements = useElements();
  const { isSuccess, isLoading } = useGetElements({ reviewCycleId });

  if (isLoading) {
    return <SkeletonLoaderMultiViewVariant />;
  }

  if (!isSuccess || elements.length === 0) return null;

  return (
    <>
      {elements.map(({ cohort, feedbackFormId, feedbackFormName }, index) => (
        <SingleViewSettings
          view={cohort}
          index={index}
          singleViewOnly={elements.length === 1}
          feedbackFormId={feedbackFormId}
          feedbackFormName={feedbackFormName}
        />
      ))}
      <Footer />
    </>
  );
};

export { Elements };
